import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faUser, 
  faChartBar,
  faSpinner,
  faExclamationTriangle,
  faClipboardCheck,
  faSync,
  faChevronLeft,
  faChevronRight,
  faMapMarkerAlt,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';
import SearchableSelect from '../../components/SearchableSelect';

const F2FProductivity = () => {
  // Prefill filters from URL (?survey_id=&supervisor_id=), e.g. opened from a supervisor's stats table.
  const initParams = new URLSearchParams(window.location.search);
  const initSurveyId = initParams.get('survey_id') || '';
  const initSupervisorId = initParams.get('supervisor_id') || '';
  const initInterviewerId = initParams.get('interviewer_id') || '';

  const [productivity, setProductivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedInterviewer, setSelectedInterviewer] = useState(initInterviewerId);
  const [selectedSupervisor, setSelectedSupervisor] = useState(initSupervisorId);
  const [selectedPerson, setSelectedPerson] = useState(
    initSupervisorId ? `sup-${initSupervisorId}` : (initInterviewerId ? `int-${initInterviewerId}` : '')
  ); // Format: 'int-{id}' or 'sup-{id}'
  // Keep the type filter on "Tous" (Enq. + Sup.); the supervisor is preselected via the
  // combined person dropdown (selectedPerson='sup-{id}'), not by forcing type=supervisor.
  const [personType, setPersonType] = useState(''); // '', 'interviewer', 'supervisor'
  const [selectedSurvey, setSelectedSurvey] = useState(initSurveyId);
  const [interviewers, setInterviewers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [stats, setStats] = useState({
    total_completes: 0,
    unique_interviewers: 0,
    unique_supervisors: 0,
    unique_fieldworkers: 0,
    unique_surveys: 0
  });

  useEffect(() => {
    fetchProductivity();
    fetchStats();
    fetchInterviewers();
    fetchSupervisors();
    fetchSurveys();
  }, [currentPage, dateFrom, dateTo, selectedInterviewer, selectedSupervisor, personType, selectedSurvey]);

  const fetchProductivity = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        date_from: dateFrom,
        date_to: dateTo,
        interviewer_id: selectedInterviewer,
        supervisor_id: selectedSupervisor,
        person_type: personType,
        survey_id: selectedSurvey
      });

      const response = await api.get(`/f2f/productivity?${params}`);
      
      if (response.data.success) {
        setProductivity(response.data.data.data || []);
        setPagination(response.data.data.pagination || {});
      }
    } catch (error) {
      console.error('Error fetching F2F productivity:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams({
        interviewer_id: selectedInterviewer,
        supervisor_id: selectedSupervisor,
        person_type: personType,
        survey_id: selectedSurvey,
        date_from: dateFrom,
        date_to: dateTo
      });

      const response = await api.get(`/f2f/productivity/stats?${params}`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchInterviewers = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedSurvey) {
        params.append('survey_id', selectedSurvey);
      }
      const url = `/f2f/productivity/interviewers${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await api.get(url);
      if (response.data.success) {
        const interviewersData = response.data.data || [];
        const interviewersArray = Array.isArray(interviewersData) ? interviewersData : Object.values(interviewersData);
        setInterviewers(interviewersArray);
      }
    } catch (error) {
      console.error('Error fetching interviewers:', error);
    }
  };

  const fetchSupervisors = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedSurvey) {
        params.append('survey_id', selectedSurvey);
      }
      if (personType === 'supervisor') {
        params.append('own_only', '1');
      }
      const url = `/f2f/productivity/supervisors${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await api.get(url);
      if (response.data.success) {
        const supervisorsData = response.data.data || [];
        const supervisorsArray = Array.isArray(supervisorsData) ? supervisorsData : Object.values(supervisorsData);
        setSupervisors(supervisorsArray);
      }
    } catch (error) {
      console.error('Error fetching supervisors:', error);
    }
  };

  const fetchSurveys = async () => {
    try {
      const response = await api.get('/f2f/productivity/surveys');
      if (response.data.success) {
        const surveysData = response.data.data || [];
        const surveysArray = Array.isArray(surveysData) ? surveysData : Object.values(surveysData);
        setSurveys(surveysArray);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'interviewer':
        setSelectedInterviewer(value);
        if (value) {
          setSelectedSupervisor(''); // Clear supervisor when interviewer is selected
          setPersonType('interviewer');
        } else if (!selectedSupervisor) {
          setPersonType('');
        }
        break;
      case 'supervisor':
        setSelectedSupervisor(value);
        if (value) {
          setSelectedInterviewer(''); // Clear interviewer when supervisor is selected
          setPersonType('supervisor');
        } else if (!selectedInterviewer) {
          setPersonType('');
        }
        break;
      case 'personType':
        setPersonType(value);
        setSelectedPerson(''); // Clear combined selection
        if (value === 'interviewer') {
          setSelectedSupervisor('');
        } else if (value === 'supervisor') {
          setSelectedInterviewer('');
        } else {
          // When "Tous" is selected, clear both individual selections
          setSelectedInterviewer('');
          setSelectedSupervisor('');
        }
        break;
      case 'person':
        setSelectedPerson(value);
        if (value) {
          // Parse the value to determine type and id
          if (value.startsWith('int-')) {
            setSelectedInterviewer(value.substring(4));
            setSelectedSupervisor('');
          } else if (value.startsWith('sup-')) {
            setSelectedSupervisor(value.substring(4));
            setSelectedInterviewer('');
          }
        } else {
          setSelectedInterviewer('');
          setSelectedSupervisor('');
        }
        break;
      case 'survey':
        setSelectedSurvey(value);
        break;
      case 'dateFrom':
        setDateFrom(value);
        break;
      case 'dateTo':
        setDateTo(value);
        break;
      default:
        break;
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await api.post('/f2f/productivity/refresh');
      
      if (response.data.success) {
        await fetchProductivity();
        await fetchStats();
        setSuccessMessage('Données de productivité F2F mises à jour avec succès!');
        setShowSuccessModal(true);
      } else {
        setSuccessMessage('Erreur lors de la mise à jour des données');
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error refreshing productivity:', error);
      setSuccessMessage('Erreur lors de la mise à jour des données');
      setShowSuccessModal(true);
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Get quota cell dimension labels for display
  const getCellLabels = (quotaCell) => {
    if (!quotaCell?.dimension_values_labels_json) return 'N/A';
    
    const labels = quotaCell.dimension_values_labels_json;
    if (!Array.isArray(labels) || labels.length === 0) return 'N/A';
    
    return labels.map(l => `${l.dimension_label}: ${l.value_label}`).join(' | ');
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background - Green theme for F2F */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #059669 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">PRODUCTIVITÉ FACE À FACE</h1>
                <p className="text-gray-600">Suivi de la productivité des enquêteurs terrain par quota assigné</p>
              </div>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors w-full sm:w-auto"
              >
                <FontAwesomeIcon 
                  icon={faSync} 
                  className={refreshing ? 'animate-spin' : ''} 
                />
                {refreshing ? 'Mise à jour...' : 'Actualiser'}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">
              <div>
                <SearchableSelect
                  options={(Array.isArray(surveys) ? surveys.filter(s => s && s.id) : []).map(s => ({ id: String(s.id), title: s.title }))}
                  selectedValue={selectedSurvey ? String(selectedSurvey) : null}
                  onChange={(val) => handleFilterChange('survey', val || '')}
                  placeholder="Toutes les enquêtes"
                  searchPlaceholder="Rechercher une enquête..."
                  showOptionMeta={false}
                  triggerClassName="px-4 py-2"
                />
              </div>
              <div>
                <select
                  value={personType}
                  onChange={(e) => handleFilterChange('personType', e.target.value)}
                  className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Tous (Enq. + Sup.)</option>
                  <option value="interviewer">Enquêteurs seulement</option>
                  <option value="supervisor">Prestataires seulement</option>
                </select>
              </div>
              <div>
                {/* Combined dropdown when "Tous" is selected */}
                {personType === '' && (
                  <SearchableSelect
                    options={[
                      ...(Array.isArray(supervisors) ? supervisors.filter(s => s && s.id) : []).map(s => ({ id: `sup-${s.id}`, name: `${s.name} (Sup.)` })),
                      ...(Array.isArray(interviewers) ? interviewers.filter(i => i && i.id) : []).map(i => ({ id: `int-${i.id}`, name: `${i.name} (Enq.)` })),
                    ]}
                    selectedValue={selectedPerson || null}
                    onChange={(val) => handleFilterChange('person', val || '')}
                    placeholder="Toutes les personnes"
                    searchPlaceholder="Rechercher une personne..."
                    getOptionLabel={(o) => o.name}
                    showOptionMeta={false}
                    triggerClassName="px-4 py-2"
                  />
                )}
                {/* Interviewers only dropdown */}
                {personType === 'interviewer' && (
                  <SearchableSelect
                    options={(Array.isArray(interviewers) ? interviewers.filter(i => i && i.id) : []).map(i => ({ id: String(i.id), name: i.name }))}
                    selectedValue={selectedInterviewer ? String(selectedInterviewer) : null}
                    onChange={(val) => handleFilterChange('interviewer', val || '')}
                    placeholder="Tous les enquêteurs"
                    searchPlaceholder="Rechercher un enquêteur..."
                    getOptionLabel={(o) => o.name}
                    showOptionMeta={false}
                    triggerClassName="px-4 py-2"
                  />
                )}
                {/* Supervisors only dropdown */}
                {personType === 'supervisor' && (
                  <SearchableSelect
                    options={(Array.isArray(supervisors) ? supervisors.filter(s => s && s.id) : []).map(s => ({ id: String(s.id), name: s.name }))}
                    selectedValue={selectedSupervisor ? String(selectedSupervisor) : null}
                    onChange={(val) => handleFilterChange('supervisor', val || '')}
                    placeholder="Tous les prestataires"
                    searchPlaceholder="Rechercher un prestataire..."
                    getOptionLabel={(o) => o.name}
                    showOptionMeta={false}
                    triggerClassName="px-4 py-2"
                  />
                )}
              </div>
              <div>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Date de début"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="w-full px-4 py-2 h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Date de fin"
                />
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 bg-emerald-100 rounded-full">
                  <FontAwesomeIcon icon={faChartBar} className="text-emerald-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Complétions</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_completes}</p>
                  <p className="text-xs text-gray-500">Quotas complétés</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-full">
                  <FontAwesomeIcon icon={faUsers} className="text-blue-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Terrain Actifs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.unique_fieldworkers || (stats.unique_interviewers + (stats.unique_supervisors || 0))}</p>
                  <p className="text-xs text-gray-500">{stats.unique_interviewers} enq. + {stats.unique_supervisors || 0} sup.</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-full">
                  <FontAwesomeIcon icon={faClipboardCheck} className="text-yellow-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Enquêtes</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.unique_surveys}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Productivity Table */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <FontAwesomeIcon icon={faSpinner} className="text-4xl text-emerald-600 animate-spin mb-4" />
                <p className="text-gray-600">Chargement de la productivité...</p>
              </div>
            ) : productivity.length === 0 ? (
              <div className="p-8 text-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-gray-400 mb-4" />
                <p className="text-gray-600">Aucune donnée de productivité trouvée</p>
                <p className="text-sm text-gray-500 mt-2">
                  Les données sont mises à jour automatiquement toutes les 15 minutes.
                  <br />
                  Seules les complétions correspondant aux quotas assignés à l'enquêteur sont comptées.
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Personne
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Enquête
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quota
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Complétions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {productivity.map((item, index) => {
                        const isSupervisor = item.supervisor_id && !item.interviewer_id;
                        const person = isSupervisor ? item.supervisor : item.interviewer;
                        
                        return (
                          <tr 
                            key={`${item.for_date}-${item.interviewer_id || 'no-int'}-${item.supervisor_id || 'no-sup'}-${item.survey_id}-${item.quota_project_id || 'no-project'}-${item.quota_cell_id || 'no-cell'}`} 
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {formatDate(item.for_date)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {person?.name || 'N/A'}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {person?.ref || ''}
                                  </div>
                                  {!isSupervisor && item.interviewer?.supervisor?.name && (
                                    <div className="text-xs text-gray-400 italic">
                                      Sous: {item.interviewer.supervisor.name}
                                    </div>
                                  )}
                                </div>
                                {isSupervisor && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-700">
                                    Sup.
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {item.survey?.title || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 max-w-xs truncate" title={getCellLabels(item.quota_cell)}>
                                {getCellLabels(item.quota_cell)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-800">
                                {item.completes || 0} complétés
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.last_page > 1 && (
                  <div className="bg-white px-4 sm:px-6 py-4 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      {/* Mobile: Show page info */}
                      <div className="sm:hidden text-sm text-gray-700 text-center w-full">
                        Page {currentPage} sur {pagination.last_page}
                      </div>
                      
                      {/* Desktop: Show full info */}
                      <div className="hidden sm:block text-sm text-gray-700">
                        <span>
                          Affichage de <span className="font-medium">{pagination.from || 0}</span> à{' '}
                          <span className="font-medium">{pagination.to || 0}</span> sur{' '}
                          <span className="font-medium">{pagination.total || 0}</span> résultats
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2 w-full sm:w-auto justify-center sm:justify-end">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                        </button>
                        
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                            let pageNum;
                            if (pagination.last_page <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= pagination.last_page - 2) {
                              pageNum = pagination.last_page - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${
                                  currentPage === pageNum
                                    ? 'bg-emerald-600 text-white border-emerald-600'
                                    : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === pagination.last_page}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />
    </div>
  );
};

export default F2FProductivity;

